import React from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useDeleteScheduleTemplate } from '@/hooks/schedules/useScheduleTemplates';
import { LoadingSpinner } from '@/components/ui/loading-states';
import type { ScheduleTemplate } from '@/types/schedule.types';

interface DeleteTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: ScheduleTemplate | null;
}

export const DeleteTemplateDialog: React.FC<DeleteTemplateDialogProps> = ({
  open,
  onOpenChange,
  template,
}) => {
  const deleteTemplate = useDeleteScheduleTemplate();
  const isLoading = deleteTemplate.isPending;

  const handleDelete = async () => {
    if (!template) return;
    
    try {
      await deleteTemplate.mutateAsync(template.id);
      onOpenChange(false);
    } catch (error) {
      // Error handling is done by the mutation hook
      console.error('Failed to delete template:', error);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-12 h-12 bg-red-100 dark:bg-red-950/20 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <DialogTitle>
                <span className="text-red-900 dark:text-red-100">Удалить шаблон графика?</span>
              </DialogTitle>
              <DialogDescription>
                Это действие нельзя будет отменить.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          <div className="bg-gray-50 dark:bg-gray-900/20 p-4 rounded-lg">
            <div className="space-y-2">
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Название:</span>
                <p className="text-sm text-gray-900 dark:text-gray-100">{template.name}</p>
              </div>

              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Тип повторения:</span>
                <p className="text-sm text-gray-900 dark:text-gray-100">
                  {template.recurrence_type === 'weekly' ? 'Еженедельный' :
                   template.recurrence_type === 'shift' ? 'Сменный' : 'Пользовательский'}
                </p>
              </div>

              {template.code_1c && (
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Код 1С:</span>
                  <p className="text-sm text-gray-900 dark:text-gray-100">{template.code_1c}</p>
                </div>
              )}

              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Источник:</span>
                <div className="mt-1">
                  <Badge variant={template.is_1c_imported ? 'secondary' : 'outline'}>
                    {template.is_1c_imported ? '1С' : 'Локальный'}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Warning for 1C templates */}
          {template.is_1c_imported && (
            <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900 rounded-md">
              <div className="flex items-start">
                <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400 mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-sm text-orange-800 dark:text-orange-200">
                  <p className="font-medium">Внимание!</p>
                  <p className="mt-1">
                    Этот шаблон импортирован из 1С. Удаление шаблона не затронет данные в 1С,
                    но он может быть синхронизирован снова при следующей синхронизации.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Show API errors */}
          {deleteTemplate.error && (
            <div className="mt-4 flex items-center p-3 text-sm text-red-700 dark:text-red-200 bg-red-100 dark:bg-red-950/20 rounded-md">
              <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>
                {(deleteTemplate.error as Error)?.message || 'Произошла ошибка при удалении'}
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Отменить
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={isLoading}
            className="min-w-[100px]"
          >
            {isLoading ? (
              <>
                <LoadingSpinner className="mr-2 h-4 w-4" />
                Удаление...
              </>
            ) : (
              'Удалить'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
